import React, { useState, useRef, useCallback, useMemo } from "react";
import Input from "react-validation/build/input";
import Form from "react-validation/build/form";
import { Modal } from "react-bootstrap";
import Select from "react-validation/build/select";
import moment from "moment";
import { required, isValidForm, NotZero } from "../../../util/validationMethods";
import { paymentState } from "../../../util/stateUtil/initialStateForComponents";
import TmReactTable from "../../utilComponents/tmReactTable/tmReactTable";
import PaymentProcess from "./paymentProcess";

// Styles
const styles = {
  modalOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalDialog: {
    margin: "auto",
    maxWidth: "500px",
    width: "100%",
  },
  modalHeader: {
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #dee2e6",
    padding: "12px 16px",
    fontWeight: "600",
  },
  modalBody: {
    padding: "20px",
  },
  formGroup: {
    marginBottom: "16px",
  },
  formRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },
  label: {
    flex: "0 0 130px",
    fontWeight: "500",
    fontSize: "13px",
    color: "#495057",
  },
  inputWrapper: {
    flex: 1,
  },
  amountWrapper: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    overflow: "hidden",
  },
  currencyIcon: {
    padding: "6px 12px",
    backgroundColor: "#e9ecef",
    borderRight: "1px solid #ced4da",
    fontWeight: "500",
    color: "#495057",
  },
  buttonRow: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #dee2e6",
  },
  deleteModal: {
    textAlign: "center",
    padding: "20px",
  },
  deleteText: {
    fontSize: "15px",
    marginBottom: "20px",
    color: "#495057",
  },
};

// Default payment object factory
const createEmptyPayment = (cctxNo, obj = null) => ({
  cctxNo,
  chequeNo: obj?.chequeNo ?? "",
  invoiceId: obj?.invoiceId ?? "",
  paymentAmt: obj?.paymentAmt ?? 0,
  paymentDate: obj?.paymentDate ?? "",
  paymentId: obj?.paymentId ?? 0,
  paymentLocation: obj?.paymentLocation ?? "",
  paymentType: obj?.paymentType ?? "",
  transactionNo: obj?.transactionNo ?? "",
  userId: obj?.userId ?? "",
  isPending: obj?.isPending ?? "",
});

// Payment type options
const PAYMENT_TYPES = [
  { value: "", label: "Select Payment Type" },
  { value: "Cash", label: "Cash" },
  { value: "Credit", label: "Credit" },
  { value: "ManualCredit", label: "Manual Credit" },
  { value: "Check", label: "Check" },
  { value: "EmployeeAdvance", label: "Employee Advance" },
  { value: "Transfer", label: "Transfer" },
];

const Payment = ({
  isDisabled,
  invoicePayments,
  invoiceId,
  paymentAccess,
  locationList,
  addUpdatePayments,
  deletePayments,
  refreshPaymentData,
  customerInfo,
}) => {
  const [payment, setPayment] = useState(() => ({
    ...paymentState,
    ...createEmptyPayment(""),
  }));
  const [modals, setModals] = useState({
    edit: false,
    delete: false,
    approval: false,
  });
  const [paymentApprovalLocation, setPaymentApprovalLocation] = useState("");
  const [showCreditPayment, setShowCreditPayment] = useState(false);
  const [creditPaymentAmount, setCreditPaymentAmount] = useState(0);

  const paymentFormRef = useRef(null);

  const handlePaymentChange = useCallback((e) => {
    const { name, value } = e.target;
    setPayment((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetPayment = useCallback((obj = null) => {
    setPayment((prev) => createEmptyPayment(prev.cctxNo, obj));
  }, []);

  const openModal = useCallback((type, data = null) => {
    if (data) {
      setPayment((prev) => ({ ...prev, ...data, paymentId: data.paymentId }));
    }
    setModals((prev) => ({ ...prev, [type]: true }));
  }, []);

  const closeModal = useCallback((type) => {
    setModals((prev) => ({ ...prev, [type]: false }));
    if (type !== "delete") resetPayment();
  }, [resetPayment]);

  const handleAddPayment = useCallback(() => {
    const form = paymentFormRef.current;
    if (form) form.validateAll();
    if (!isValidForm(form)) return;

    const isCreditPayment =
      payment.paymentType === "Credit" && (!payment.paymentId || payment.paymentId === 0);

    if (isCreditPayment) {
      closeModal("edit");
      setCreditPaymentAmount(payment.paymentAmt);
      setShowCreditPayment(true);
      return;
    }

    addUpdatePayments(invoiceId, {
      cctxNo: payment.cctxNo,
      chequeNo: payment.chequeNo,
      invoiceId: payment.invoiceId,
      paymentAmt: payment.paymentAmt,
      paymentDate: payment.paymentDate || moment().format("YYYY-MM-DD HH:mm:ss"),
      paymentId: payment.paymentId,
      paymentLocation: payment.paymentLocation,
      paymentType: payment.paymentType,
      transactionNo: payment.transactionNo,
      userId: payment.userId,
      isPending: payment.isPending,
    });

    closeModal("edit");
    closeModal("approval");
  }, [payment, invoiceId, addUpdatePayments, closeModal]);

  const handleDelete = useCallback(() => {
    deletePayments(payment.paymentId, invoiceId);
    setModals((prev) => ({ ...prev, delete: false }));
    setPayment((prev) => ({ ...prev, paymentId: 0 }));
  }, [payment.paymentId, invoiceId, deletePayments]);

  const handleCreditPaymentComplete = useCallback(function () {
    setShowCreditPayment(false);
    if (refreshPaymentData) {
      refreshPaymentData();
    } else {
      window.location.reload();
    }
  }, [refreshPaymentData]);

  const handleCreditPaymentCancel = useCallback(function () {
    setShowCreditPayment(false);
  }, []);

  const columns = useMemo(
    () => [
      {
        accessor: "userId",
        Header: "USER ID",
        style: { textAlign: "center" },
      },
      {
        accessor: "paymentDate",
        Header: "DATE",
        style: { textAlign: "center" },
      },
      {
        accessor: "paymentType",
        Header: "PAYMENT TYPE",
        style: { textAlign: "center" },
      },
      {
        accessor: "paymentAmt",
        Header: "AMOUNT",
        style: { textAlign: "center" },
      },
      {
        accessor: "isPending",
        Header: "STATUS",
        style: { textAlign: "center" },
        className: "color-blue-grey-lighter",
        Cell: ({ value }) => <div>{value ? "Pending" : "Approved"}</div>,
      },
      {
        accessor: "Actions",
        Header: "Actions",
        sortable: false,
        filterable: false,
        style: { textAlign: "center" },
        Cell: ({ original }) => (
          <div>
            {paymentAccess.edit && !original.isPending && (
              <input
                type="button"
                disabled={!paymentAccess.edit || isDisabled}
                className="btn btn-sm btn-primary mr-10"
                value="Edit"
                onClick={() => {
                  resetPayment(original);
                  openModal("edit");
                }}
              />
            )}
            {paymentAccess.delete && !original.isPending && (
              <input
                type="button"
                disabled={!paymentAccess.delete || isDisabled}
                className="btn btn-sm btn-danger"
                value="Delete"
                onClick={() => {
                  setPayment((prev) => ({ ...prev, paymentId: original.paymentId }));
                  openModal("delete");
                }}
              />
            )}
            &nbsp;&nbsp;
            {paymentAccess.add && original.isPending && (
              <button
                type="button"
                disabled={!paymentAccess.add || isDisabled}
                className="btn btn-sm btn-success"
                onClick={() => {
                  setPayment((prev) => ({ ...prev, paymentId: original.paymentId }));
                  openModal("approval");
                }}
              >
                <i className="fa fa-check" aria-hidden="true" /> Add
              </button>
            )}
          </div>
        ),
      },
    ],
    [paymentAccess, isDisabled, resetPayment, openModal]
  );

  const isNewPayment = !payment.paymentId || payment.paymentId === 0;

  return (
    <>
      <Form ref={paymentFormRef}>
        <div className="pd-10">
          <h6 className="text-blue text-bg-color form-group">Payments</h6>
          <div className="text-right">
            <input
              type="button"
              disabled={isDisabled}
              className="btn btn-sm btn-success"
              value="Add Payment"
              onClick={() => {
                resetPayment();
                openModal("edit");
              }}
            />
          </div>
          <div className="row">
            <div className="col-md-12 col-sm-12 mb-10">
              <TmReactTable
                filterable
                sortable
                resizable
                defaultPageSize={25}
                data={invoicePayments}
                columns={columns}
              />
            </div>
          </div>
        </div>
      </Form>

      {/* Delete Confirmation Modal */}
      <Modal show={modals.delete} centered dialogClassName="modal-dialog-centered">
        <div style={styles.modalHeader}>Delete Payment</div>
        <div style={styles.deleteModal}>
          <p style={styles.deleteText}>Do you want to delete the payment?</p>
          <div style={styles.buttonRow}>
            <button type="button" className="btn btn-success btn-sm" onClick={handleDelete}>Yes</button>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => closeModal("delete")}>No</button>
          </div>
        </div>
      </Modal>

      {/* Edit/Add Payment Modal */}
      <Modal show={modals.edit} centered dialogClassName="modal-dialog-centered">
        <div style={styles.modalHeader}>
          {isNewPayment ? "Add Payment" : "Update Payment"}
        </div>
        <div style={styles.modalBody}>
          <Form ref={paymentFormRef}>
            <div style={styles.formRow}>
              <label style={styles.label}>PAYMENT TYPE</label>
              <div style={styles.inputWrapper}>
                <Select
                  className="form-control"
                  validations={[required]}
                  requiredmsg="Please Select Payment Type."
                  onChange={handlePaymentChange}
                  disabled={isDisabled}
                  name="paymentType"
                  value={payment.paymentType}
                >
                  {PAYMENT_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
            </div>

            {payment.paymentType === "Check" && (
              <div style={styles.formRow}>
                <label style={styles.label}>CHECK #</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    name="chequeNo"
                    placeholder="Enter check number"
                    onChange={handlePaymentChange}
                    disabled={isDisabled}
                    className="form-control"
                    value={payment.chequeNo}
                  />
                </div>
              </div>
            )}

            <div style={styles.formRow}>
              <label style={styles.label}>AMOUNT</label>
              <div style={styles.inputWrapper}>
                <div style={styles.amountWrapper}>
                  <span style={styles.currencyIcon}>$</span>
                  <Input
                    type="number"
                    validations={[required, NotZero]}
                    requiredmsg="Please Enter Payment Amount."
                    disabled={isDisabled}
                    onChange={handlePaymentChange}
                    className="form-control"
                    name="paymentAmt"
                    value={payment.paymentAmt}
                    style={{ border: "none", boxShadow: "none" }}
                  />
                </div>
              </div>
            </div>

            <div style={styles.buttonRow}>
              <input
                type="button"
                disabled={isDisabled}
                className="btn btn-sm btn-success"
                value={isNewPayment ? "Add Payment" : "Update Payment"}
                onClick={handleAddPayment}
              />
              <input
                type="button"
                className="btn btn-sm btn-danger"
                disabled={isDisabled}
                onClick={() => closeModal("edit")}
                value="Close"
              />
            </div>
          </Form>
        </div>
      </Modal>

      {/* Payment Approval Modal */}
      <Modal show={modals.approval} centered dialogClassName="modal-dialog-centered">
        <div style={styles.modalHeader}>Confirm Payment Approval</div>
        <div style={styles.modalBody}>
          <p style={{ ...styles.deleteText, textAlign: "center" }}>
            Are you sure you want to approve this payment?
          </p>
          <div style={styles.formRow}>
            <label style={styles.label}>LOCATION</label>
            <div style={styles.inputWrapper}>
              <select
                onChange={(e) => setPaymentApprovalLocation(e.target.value)}
                className="form-control"
                value={paymentApprovalLocation}
              >
                <option value="">Select Location</option>
                {locationList?.map((item, index) => (
                  <option key={index} value={item.location}>{item.location}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={styles.buttonRow}>
            <input
              type="button"
              disabled={isDisabled}
              className="btn btn-sm btn-success"
              value="Approve Payment"
              onClick={handleAddPayment}
            />
            <input
              type="button"
              className="btn btn-sm btn-danger"
              disabled={isDisabled}
              onClick={() => closeModal("approval")}
              value="Close"
            />
          </div>
        </div>
      </Modal>

      {/* Inline Credit Card Payment */}
      {showCreditPayment && (
        <PaymentProcess
          invoiceNumber={invoiceId}
          paymentAmount={creditPaymentAmount}
          paymentLocation={payment.paymentLocation}
          customerInfo={customerInfo}
          onComplete={handleCreditPaymentComplete}
          onCancel={handleCreditPaymentCancel}
        />
      )}
    </>
  );
};

export default Payment;
